import { useState, useMemo } from "react";
import { Bar } from "react-chartjs-2";
import { useTranslation } from "react-i18next";
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  Tooltip,
  Legend,
} from "chart.js";
import { formattedColorMaterial } from "../../../components/formatted/FormattedText";
import { normalizeMaterialNameFromTranslation } from "../../../shared/utils/materialNameNormalizer";
import CampaignAnalysisChartCard from "./CampaignAnalysisChartCard";
import { CHART_COLORS, CHART_HEIGHTS } from "./campaignChartConstants";

ChartJS.register(CategoryScale, LinearScale, BarElement, Tooltip, Legend);

const getMonthStart = (date) => {
  const d = new Date(date);
  const monthStart = new Date(d.getFullYear(), d.getMonth(), 1);
  monthStart.setHours(0, 0, 0, 0);
  return monthStart;
};

const formatMonthLabel = (monthStart, t) => {
  const monthNames = [
    t("January"),
    t("February"),
    t("March"),
    t("April"),
    t("May"),
    t("June"),
    t("July"),
    t("August"),
    t("September"),
    t("October"),
    t("November"),
    t("December"),
  ];
  return monthNames[monthStart.getMonth()];
};

const getReferenceNameFromCls = (cls) => {
  if (!cls) return cls;

  try {
    const materialListStr = localStorage.getItem("materialsDescription");
    if (!materialListStr) return cls;

    const materialList = JSON.parse(materialListStr);
    const clsLower = cls.toLowerCase();

    for (const mat of materialList) {
      if (mat.referenceName?.toLowerCase() === clsLower) {
        return mat.referenceName;
      }
      if (mat.possibleNames?.some((name) => name?.toLowerCase() === clsLower)) {
        return mat.referenceName;
      }
    }
  } catch (error) {
    if (process.env.NODE_ENV === "development") {
      console.warn("Error parsing materialsDescription:", error);
    }
  }

  return cls;
};

const CampaignMaterialRemainingChart = ({
  trackedMaterials,
  monthlyTrackedObjects,
  comments,
  campaignPeriod,
}) => {
  const { t } = useTranslation();
  const [selectedMaterial, setSelectedMaterial] = useState(
    trackedMaterials && trackedMaterials.length > 0
      ? normalizeMaterialNameFromTranslation(trackedMaterials[0]).toLowerCase()
      : null,
  );

  const normalizedMaterial = selectedMaterial
    ? normalizeMaterialNameFromTranslation(selectedMaterial).toLowerCase()
    : null;

  const hasMonthlyData =
    monthlyTrackedObjects &&
    Array.isArray(monthlyTrackedObjects) &&
    monthlyTrackedObjects.length > 0;

  const chartData = useMemo(() => {
    if (
      !selectedMaterial ||
      !trackedMaterials ||
      trackedMaterials.length === 0 ||
      !campaignPeriod ||
      !normalizedMaterial
    ) {
      return {
        labels: [],
        datasets: [],
        yAxisMax: 10,
        yAxisStep: 2,
      };
    }

    const monthlyData = {};
    const monthDateMap = {};
    const startDate = new Date(campaignPeriod.start);
    const stopDate = new Date(campaignPeriod.stop);
    const currentDate = new Date(startDate);

    while (currentDate <= stopDate) {
      const monthStart = getMonthStart(currentDate);
      const monthKey = `${monthStart.getFullYear()}-${monthStart.getMonth()}`;
      if (!monthlyData[monthKey]) {
        monthlyData[monthKey] = {
          materialObjectsTracked: 0,
          materialObjectsRemoved: 0,
          totalObjectsTracked: 0,
          totalObjectsRemoved: 0,
        };
        monthDateMap[monthKey] = monthStart;
      }
      currentDate.setDate(currentDate.getDate() + 1);
    }

    comments.forEach((comment) => {
      if (!comment.material) {
        return;
      }
      const date = new Date(comment.notificationDate || comment.time);
      const monthStart = getMonthStart(date);
      const monthKey = `${monthStart.getFullYear()}-${monthStart.getMonth()}`;
      if (monthlyData[monthKey] !== undefined) {
        const number = Number(comment.number) || 0;
        const commentMaterial = normalizeMaterialNameFromTranslation(
          comment.material,
        ).toLowerCase();

        if (
          commentMaterial === normalizedMaterial &&
          comment.objectRemoved === true
        ) {
          monthlyData[monthKey].materialObjectsRemoved += number;
        }

        if (comment.objectRemoved === true) {
          monthlyData[monthKey].totalObjectsRemoved += number;
        }
      }
    });

    if (!hasMonthlyData) {
      return { labels: [], datasets: [], yAxisMax: 10, yAxisStep: 2 };
    }

    monthlyTrackedObjects.forEach((monthEntry) => {
      const monthKey = `${monthEntry.year}-${monthEntry.month - 1}`;

      if (monthlyData[monthKey] !== undefined) {
        const wasteData = monthEntry.wasteData || {};

        let totalTracked = 0;
        let materialTracked = 0;

        Object.keys(wasteData).forEach((materialKey) => {
          const count = wasteData[materialKey] || 0;
          totalTracked += count;

          const referenceName = getReferenceNameFromCls(materialKey);
          if (referenceName?.toLowerCase() === normalizedMaterial) {
            materialTracked += count;
          }
        });

        monthlyData[monthKey].totalObjectsTracked = totalTracked;
        monthlyData[monthKey].materialObjectsTracked = materialTracked;

        monthlyData[monthKey].materialObjectsTracked = Math.max(
          monthlyData[monthKey].materialObjectsTracked,
          monthlyData[monthKey].materialObjectsRemoved,
        );
        monthlyData[monthKey].totalObjectsTracked = Math.max(
          monthlyData[monthKey].totalObjectsTracked,
          monthlyData[monthKey].totalObjectsRemoved,
        );
      }
    });

    const sortedMonths = Object.keys(monthlyData).sort((a, b) => {
      const [yearA, monthA] = a.split("-").map(Number);
      const [yearB, monthB] = b.split("-").map(Number);
      if (yearA !== yearB) return yearA - yearB;
      return monthA - monthB;
    });

    const materialColor = formattedColorMaterial(normalizedMaterial);

    const remainingRatios = sortedMonths.map((monthKey) => {
      const monthData = monthlyData[monthKey];
      const materialRemaining =
        monthData.materialObjectsTracked - monthData.materialObjectsRemoved;
      const totalObjectsRemaining =
        monthData.totalObjectsTracked - monthData.totalObjectsRemoved;

      if (totalObjectsRemaining > 0) {
        return (materialRemaining / totalObjectsRemaining) * 100;
      }
      return 0;
    });

    const maxRatio = Math.max(...remainingRatios, 0);

    const calculateYAxisConfig = (max) => {
      if (max === 0) {
        return { max: 10, step: 2 };
      }

      const roundUpToNiceNumber = (value) => {
        if (value <= 10) return 10;
        if (value <= 20) return 20;
        if (value <= 30) return 30;
        if (value <= 50) return 50;
        if (value <= 100) return 100;

        const magnitude = Math.pow(10, Math.floor(Math.log10(value)));
        const normalized = value / magnitude;
        let multiplier;
        if (normalized <= 1) multiplier = 1;
        else if (normalized <= 2) multiplier = 2;
        else if (normalized <= 5) multiplier = 5;
        else multiplier = 10;
        return multiplier * magnitude;
      };

      const niceMax = roundUpToNiceNumber(max);
      const stepSize = niceMax / 5;
      const yAxisMax = stepSize * 5;

      return { max: yAxisMax, step: stepSize };
    };

    const { max: yAxisMax, step: yAxisStep } = calculateYAxisConfig(maxRatio);

    return {
      labels: sortedMonths.map((monthKey) =>
        formatMonthLabel(monthDateMap[monthKey], t),
      ),
      datasets: [
        {
          label: t("Material remaining ratio (%)"),
          data: remainingRatios,
          backgroundColor: materialColor || CHART_COLORS.PRIMARY,
          borderColor: materialColor || CHART_COLORS.PRIMARY,
          borderWidth: 1,
        },
      ],
      yAxisMax,
      yAxisStep,
    };
  }, [
    selectedMaterial,
    normalizedMaterial,
    trackedMaterials,
    monthlyTrackedObjects,
    hasMonthlyData,
    comments,
    campaignPeriod,
    t,
  ]);

  const options = useMemo(
    () => ({
      responsive: true,
      maintainAspectRatio: false,
      plugins: {
        legend: { display: false },
        datalabels: { display: false },
        tooltip: {
          backgroundColor: CHART_COLORS.TOOLTIP,
          titleAlign: "center",
          callbacks: {
            label: (context) =>
              `${context.dataset.label}: ${context.parsed.y.toFixed(2)}%`,
          },
        },
      },
      scales: {
        y: {
          beginAtZero: true,
          max: chartData.yAxisMax || 10,
          ticks: {
            stepSize: chartData.yAxisStep || 2,
            precision: 0,
            callback: (value) => `${Math.round(value)}%`,
          },
          title: {
            display: true,
            text: t("Remaining ratio (%)"),
          },
        },
        x: {
          ticks: {
            autoSkip: false,
            maxRotation: 45,
            minRotation: 45,
          },
        },
      },
    }),
    [chartData.yAxisMax, chartData.yAxisStep, t],
  );

  const title = t("campaign.flowQualityChartTitle");
  const chartHeight = CHART_HEIGHTS.LARGE;

  const materialSelect = (
    <label className="campaign-analysis-card__select-wrap">
      <span className="campaign-analysis-card__select-label">
        {t("campaign.distributionMaterialLabel")}
      </span>
      <select
        className="campaign-analysis-card__select"
        value={selectedMaterial || ""}
        onChange={(event) => setSelectedMaterial(event.target.value)}
      >
        {trackedMaterials.map((material) => {
          const normalizedValue =
            normalizeMaterialNameFromTranslation(material).toLowerCase();
          return (
            <option key={material} value={normalizedValue}>
              {t(material)}
            </option>
          );
        })}
      </select>
    </label>
  );

  const renderContent = (content) => (
    <CampaignAnalysisChartCard title={title} headerAction={materialSelect}>
      {content}
    </CampaignAnalysisChartCard>
  );

  if (!trackedMaterials || trackedMaterials.length === 0 || !campaignPeriod) {
    return renderContent(
      <div
        className="campaign-analysis-card__empty"
        style={{ height: chartHeight }}
      >
        {t("No data available")}
      </div>,
    );
  }

  if (!hasMonthlyData) {
    return renderContent(
      <div
        className="campaign-analysis-card__empty"
        style={{ height: chartHeight }}
      >
        {t("Loading...")}
      </div>,
    );
  }

  if (chartData.labels.length === 0) {
    return renderContent(
      <div
        className="campaign-analysis-card__empty"
        style={{ height: chartHeight }}
      >
        {t("No data available")}
      </div>,
    );
  }

  return renderContent(
    <div
      className="campaign-analysis-card__chart"
      style={{ height: chartHeight }}
    >
      <Bar data={chartData} options={options} />
    </div>,
  );
};

export default CampaignMaterialRemainingChart;
